/**
 * Role-based access control configuration
 *
 * Defines which pages each role can access.
 * Admin has access to all pages (*)
 * Other roles have specific page restrictions
 */

const basePages = [
  "/",
  "/dashboard",
  "/profile",
  "/settings",
  "/account/profile",
  "/notifications",
];

/** Pages that require an explicit profile grant (admin still needs grant; fullAccess bypasses). */
export const PROFILE_ONLY_PAGES = ["/epc"];

const isProfileOnlyPage = (pageName) => {
  const normalized =
    pageName && pageName.length > 1 && pageName.endsWith("/")
      ? pageName.slice(0, -1)
      : pageName;
  return PROFILE_ONLY_PAGES.some((page) => matchesPage(page, normalized));
};

const hasExplicitPageGrant = (pageName, userPages = []) =>
  (userPages || []).some((allowedPage) => matchesPage(allowedPage, pageName));

const roles = {
  admin: {
    allowedPages: "*", // Admin can access all pages
  },

  company: {
    allowedPages: basePages,
  },

  employee: {
    allowedPages: basePages,
  },

  user: {
    allowedPages: basePages,
  },
};

/**
 * Normalize allowed page token (strip permission suffix like ':write')
 */
const normalizeAllowedPage = (page) => {
  if (!page) return "";
  const [base] = page.split(":");
  return base;
};

/**
 * Check if a page path matches an allowed page pattern
 */
const matchesPage = (allowedPage, pageName) => {
  const basePage = normalizeAllowedPage(allowedPage);
  if (!basePage) return false;
  const normalizedPageName =
    pageName && pageName.length > 1 && pageName.endsWith("/")
      ? pageName.slice(0, -1)
      : pageName;

  if (basePage.endsWith("/*")) {
    const prefix = basePage.slice(0, -2);
    return normalizedPageName.startsWith(prefix);
  }
  // Exact match OR any sub-route (e.g. /products grants /products/edit/123)
  return (
    normalizedPageName === basePage ||
    normalizedPageName.startsWith(basePage + "/")
  );
};

/**
 * Check if a user has access to a specific page
 *
 * @param {string} userRole - The user's role (admin, employee, user)
 * @param {Array} userModules - Array of modules assigned to the user
 * @param {string} pageName - The page being accessed
 * @param {Array} userPages - Array of explicitly allowed page paths for this user
 * @returns {boolean} - Whether the user has access
 */
export const hasPageAccess = (
  userRole,
  userModules = [],
  pageName,
  userPages = [],
  fullAccess = false,
) => {
  if (fullAccess) return true;

  if (isProfileOnlyPage(pageName)) {
    return hasExplicitPageGrant(pageName, userPages);
  }

  const normalizedRole = userRole?.toLowerCase();
  const roleConfig = roles[normalizedRole];

  if (!roleConfig) {
    return false;
  }

  // Admin has access to everything
  if (roleConfig.allowedPages === "*") {
    return true;
  }

  // Check explicit per-page access for this user (highest priority)
  if (userPages && userPages.length > 0) {
    const hasDirectAccess = userPages.some((allowedPage) =>
      matchesPage(allowedPage, pageName),
    );
    if (hasDirectAccess) return true;
  }

  // Check basic role-based pages (dashboard, profile, settings always allowed)
  const hasRoleAccess = roleConfig.allowedPages.some((allowedPage) =>
    matchesPage(allowedPage, pageName),
  );
  if (hasRoleAccess) return true;

  return false;
};

/**
 * Check if a user has WRITE access to a specific page.
 *
 * Convention:
 * - Read access:   "/products"
 * - Write access:  "/products:write" (also implies read)
 */
export const hasPageWriteAccess = (
  userRole,
  pageName,
  userPages = [],
  fullAccess = false,
) => {
  if (fullAccess) return true;

  if (isProfileOnlyPage(pageName)) {
    return (userPages || []).some((token) => {
      const [base, permission] = token.split(":");
      return permission === "write" && matchesPage(base, pageName);
    });
  }

  const normalizedRole = userRole?.toLowerCase();

  // Admin can modify everything
  if (normalizedRole === "admin") {
    return true;
  }

  if (!userPages || userPages.length === 0) {
    return false;
  }

  // Look for tokens that explicitly grant write on this page
  return userPages.some((token) => {
    const [base, permission] = token.split(":");
    if (permission !== "write") return false;
    return matchesPage(base, pageName);
  });
};

/** Shorthand when you have the full user object from auth */
export const hasPageAccessForUser = (user, pageName, userModules = []) => {
  if (!user) return false;
  return hasPageAccess(
    user.role,
    userModules,
    pageName,
    user.pages || [],
    !!user.fullAccess,
  );
};

export const hasPageWriteAccessForUser = (user, pageName) => {
  if (!user) return false;
  return hasPageWriteAccess(
    user.role,
    pageName,
    user.pages || [],
    !!user.fullAccess,
  );
};

/** True for system admin (role=admin), fullAccess profiles, or users with /settings/notifications page access */
export const isNotificationAdmin = (user) => {
  if (!user) return false;
  if (user.role === "admin" || user.fullAccess) return true;
  return (user.pages || []).some(
    (page) =>
      page === "/settings/notifications" ||
      page === "/settings/notifications:write",
  );
};

/** Dashboard duruş widget — Duruş Kayıtları only (not EPC, not reports). */
export const DOWNTIME_ACCESS_PAGES = ["/machine-stop-logs"];

export const hasDowntimeAccessForUser = (user) =>
  !!user &&
  DOWNTIME_ACCESS_PAGES.some((page) => hasPageAccessForUser(user, page));

// Kept only for backward compatibility; real checks use page access now.
export const hasModuleAccess = (userRole, userModules = [], requiredModule) => {
  if (userRole?.toLowerCase() === "admin") {
    return true;
  }
  return false;
};

export default roles;
